/**
 *  A NAPLO FŐMENÜJE – egy helyen: mi hova visz, és melyik képernyő joga kell hozzá.
 *
 *  A felület a könyvelőprogramok bevált menüjét követi: felül néhány FŐMENÜ (Törzsadatok,
 *  Könyvelés, Naplók, …), alattuk a kiválasztott főmenü ALMENÜI csempékként. A régi, hosszú
 *  bal oldali menüt ez váltja ki.
 *
 *  EGYSZERES ÉS KETTŐS KÖNYVELÉS KÖZÖS MENÜN. Ugyanaz a menüpont a két módban más-más
 *  nézetet nyithat (`ut` / `utKettos`), és van, ami csak az egyik módban értelmes (`mod`).
 *  Így a felhasználó egy menüt tanul meg; a mód csak a képernyők kinézetét változtatja.
 *
 *  JOGOSULTSÁG. A `jog` a jogkörszerkesztőben (Jogosultságok → NAPLO) adható képernyő-url.
 *  A régi oldalak url-je maradt a jog kulcsa, hogy a már kiosztott jogok érvényesek maradjanak.
 *  Ugyanezt a listát nézi a kiszolgáló is (backend/naplo-kapu.js) és a jogkörszerkesztő
 *  katalógusa is (backend/jogosultsag.js, BELSO_PONTOK.NAPLO) – új menüpontnál mindhármat bővítsd.
 */

export type KonyvelesMod = 'egyszeres' | 'kettos';

export interface NaploMenupont {
  kulcs: string;
  cim: string;
  leiras: string;
  ikon: string;
  /** Egyszeres könyvelésnél (és ha nincs `utKettos`, mindkettőnél) ide visz. */
  ut: string;
  query?: Record<string, string>;
  /** Kettős könyvelésnél más nézet nyílik. */
  utKettos?: string;
  queryKettos?: Record<string, string>;
  /** Csak ebben a könyvelési módban jelenik meg. */
  mod?: KonyvelesMod;
  /** Csak akkor, ha az ügyfélnél be van kapcsolva a bank (nem mindenkinél kell bankot vezetni). */
  bankFuggo?: boolean;
  /** Csak ilyen típusú ügyfélnél (pl. társasház). */
  ugyfelTipus?: string;
  /** A jogkörszerkesztőben adható képernyő-jog. */
  jog: string;
  /** Másik alrendszerbe visz (pl. ESZKOZ): a csempe az alrendszert is átváltja, nem csak navigál. */
  alrendszer?: string;
}

export interface NaploFomenu {
  kulcs: string;
  cim: string;
  ikon: string;
  pontok: NaploMenupont[];
}

export const NAPLO_FOMENUK: NaploFomenu[] = [
  {
    kulcs: 'torzs', cim: 'Törzsadatok', ikon: 'albums-outline',
    pontok: [
      { kulcs: 'ugyfelek', cim: 'Ügyfelek', leiras: 'Ügyfél választása, felvétele, adatai', ikon: 'business-outline', ut: '/naplo/ugyfelek', jog: '/naplo/ugyfelek' },
      { kulcs: 'partner', cim: 'Partnertörzs', leiras: 'Vevők és szállítók', ikon: 'people-outline',
        ut: '/naplo/torzsek', query: { nezet: 'partner' }, jog: '/naplo/torzsek' },
      { kulcs: 'rovat', cim: 'Rovatok', leiras: 'A naplófőkönyv rovatai (bevétel, kiadás, ÁFA)', ikon: 'list-outline',
        ut: '/naplo/torzsek', query: { nezet: 'rovat' }, mod: 'egyszeres', jog: '/naplo/torzsek' },
      { kulcs: 'oszlop', cim: 'Oszlopok', leiras: 'A naplófőkönyv oszlopai', ikon: 'grid-outline',
        ut: '/naplo/torzsek', query: { nezet: 'oszlop' }, mod: 'egyszeres', jog: '/naplo/torzsek' },
      { kulcs: 'tukor', cim: 'Főkönyvi számlatükör', leiras: 'Főkönyvi számlák, gyűjtők', ikon: 'git-network-outline',
        ut: '/naplo/kettos', query: { nezet: 'tukor' }, mod: 'kettos', jog: '/naplo/torzsek' },
      { kulcs: 'penztar', cim: 'Pénztár és bank', leiras: 'Pénzforgalmi helyek', ikon: 'wallet-outline',
        ut: '/naplo/torzsek', query: { nezet: 'penztar' }, mod: 'egyszeres', jog: '/naplo/torzsek' },
      { kulcs: 'gyujto', cim: 'Gyűjtők', leiras: 'Tételek csoportosítása', ikon: 'file-tray-stacked-outline',
        ut: '/naplo/torzsek', query: { nezet: 'gyujto' }, mod: 'egyszeres', jog: '/naplo/torzsek' },
      { kulcs: 'navtorzs', cim: 'NAV törzsadat', leiras: 'A cég adatai a NAV nyilvántartásából', ikon: 'id-card-outline',
        ut: '/naplo/torzsek', query: { nezet: 'navtorzs' }, jog: '/naplo/torzsek' },
      { kulcs: 'bevsor', cim: 'ÁFA-bevallási sorok', leiras: 'A számlasorok bevallási sora ebből választható', ikon: 'receipt-outline',
        ut: '/naplo/torzsek', query: { nezet: 'bevsor' }, jog: '/naplo/torzsek' },
      //  ADÓKÓDOK: a bevallási sor párja – ügyfelenkénti törzs, a számlasor ablaka ebből választ
      //  (fordított adózásnál a fizetendő és a levonható oldalon is ez azonosít).
      { kulcs: 'adokod', cim: 'Adókódok (NAV)', leiras: 'A NAV szabványos adókódjai (eÁFA) – a számlasor ebből választ, a NAV-tól frissíthető', ikon: 'pricetag-outline',
        ut: '/naplo/torzsek', query: { nezet: 'adokod' }, jog: '/naplo/torzsek' },
      //  MUNKASZÁMOK: munka / telephely / projekt szerinti gyűjtés (a kivonat alábontja rájuk).
      { kulcs: 'munkaszam', cim: 'Munkaszámok', leiras: 'Munka, telephely, projekt szerinti gyűjtés – a kivonat alábontható rájuk', ikon: 'briefcase-outline',
        ut: '/naplo/torzsek', query: { nezet: 'munkaszam' }, mod: 'kettos', jog: '/naplo/torzsek' },
      { kulcs: 'beallitas', cim: 'Könyvelési beállítások', leiras: 'Ellenszámlák, nyitó/záró számlák', ikon: 'options-outline',
        ut: '/naplo/kettos', query: { nezet: 'beallitas' }, mod: 'kettos', jog: '/naplo/torzsek' },
    ],
  },
  {
    kulcs: 'konyveles', cim: 'Könyvelés', ikon: 'create-outline',
    pontok: [
      { kulcs: 'konyveles', cim: 'Könyvelés', leiras: 'Tételek rögzítése', ikon: 'create-outline',
        ut: '/naplo/konyveles', utKettos: '/naplo/kettos', queryKettos: { nezet: 'kontir' }, jog: '/naplo/konyveles' },
      // Nincs külön „Online Számla" oldal: a számla behúzása a könyvelőablakban nyílik (ugyanaz a
      // választó mindkét módban, osa-valaszto), és a NAV-lekérés ott magától indul. A NAV-kulcsok
      // a Törzsadatok → NAV technikai felhasználó alatt.
      { kulcs: 'behuzas', cim: 'Számla behúzása', leiras: 'NAV Online Számla – a letöltött számlákból; friss lekérés gombbal', ikon: 'cloud-download-outline',
        ut: '/naplo/konyveles', query: { osa: '1' }, utKettos: '/naplo/kettos', queryKettos: { nezet: 'osa' }, jog: '/naplo/konyveles' },
      { kulcs: 'dokumentumtar', cim: 'Dokumentumtár', leiras: 'Beérkezett számlák, e-mailek', ikon: 'folder-open-outline', ut: '/naplo/dokumentumtar', jog: '/naplo/dokumentumtar' },
      { kulcs: 'kontir', cim: 'Bank kontírozás', leiras: 'Banki tételek kontírozása, párosítása', ikon: 'git-compare-outline', ut: '/naplo/kontir', jog: '/naplo/kontir' },
      { kulcs: 'opg', cim: 'Pénztárgép', leiras: 'Napi zárások (NAV OPG)', ikon: 'calculator-outline', ut: '/naplo/opg', jog: '/naplo/opg' },
      { kulcs: 'tarsashaz', cim: 'Társasház', leiras: 'Albetétek, közös költség', ikon: 'home-outline', ut: '/naplo/tarsashaz', ugyfelTipus: 'tarsashaz', jog: '/naplo/tarsashaz' },
      { kulcs: 'ingatlan', cim: 'Ingatlan-bérbeadás', leiras: 'Ingatlanok, bérletek, előleg és befizetések', ikon: 'key-outline', ut: '/naplo/ingatlan', ugyfelTipus: 'ingatlan', jog: '/naplo/ingatlan' },
      { kulcs: 'zaras', cim: 'Nyitás / zárás', leiras: 'Évnyitás és évzárás', ikon: 'lock-closed-outline',
        ut: '/naplo/kettos', query: { nezet: 'zaras' }, mod: 'kettos', jog: '/naplo/konyveles' },
      //  Tárgyi eszközök: ÖNÁLLÓ modul (ESZKOZ), ugyanazokkal az ügyfelekkel – ez csak átugrás oda.
      //  A jogát a Jogosultságok → ESZKOZ adja; a kiszolgáló az ESZKOZ-kapun ellenőrzi.
      { kulcs: 'eszkoz', cim: 'Tárgyi eszközök', leiras: 'Önálló modul: nyilvántartás, értékcsökkenés, kivezetés', ikon: 'construct-outline',
        ut: '/eszkoz/eszkozok', jog: '/naplo/konyveles', alrendszer: 'ESZKOZ' },
      //  Számlázó: ÖNÁLLÓ modul (SZAMLAZO) – a cég saját számlázása; a könyvelő ott csak olvas. Átugrás oda,
      //  ugyanazzal az aktív céggel (KozosCegService).
      { kulcs: 'szamlazo', cim: 'Számla és Kassza', leiras: 'Az ügyfél kiállított számlái, díjbekérői (olvasás)', ikon: 'receipt-outline',
        ut: '/szamlazo/szamlak', jog: '/naplo/konyveles', alrendszer: 'SZAMLAZO' },
    ],
  },
  {
    kulcs: 'naplok', cim: 'Naplók', ikon: 'reader-outline',
    pontok: [
      // A naplók sorrendje az RLB-ét követi; egy sorra kattintva a rács alatti gombsorban vannak
      // a műveletek (módosítás, törlés, kiegyenlítés, rendezések, számlakép, jegyzet …).
      //  Egyszeres könyvvitelben a pénzmozgást a Naplófőkönyv és a Házipénztár vezeti – külön
      //  „pénzügyi nyilvántartás" és pénztár/bank napló csak a kettős könyvvitelben van.
      //  RLB-IMPORT: a három RLB-export (pénzügyi nyilvántartás, pénztár-bank és vegyes napló) egy
      //  feltöltéssel – a program ismeri fel, melyik melyik (backend/naplo-rlb-import.js).
      { kulcs: 'rlbimport', cim: 'RLB-import', leiras: 'A pénzügyi nyilvántartás, a pénztár-bank és a vegyes napló exportja egyszerre – úgy kerül be, mintha itt könyvelted volna', ikon: 'cloud-upload-outline',
        ut: '/naplo/naplok', query: { ful: 'penzugyi', rlbimport: '1' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'penzugyi', cim: 'Pénzügyi nyilvántartás', leiras: 'Vevő- és szállítószámlák, rendezettség', ikon: 'documents-outline',
        ut: '/naplo/naplok', query: { ful: 'penzugyi' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'penzbank', cim: 'Pénztár/Bank napló', leiras: 'Banki és pénztári mozgások egyenleggel', ikon: 'cash-outline',
        ut: '/naplo/naplok', query: { ful: 'bank' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'vegyes', cim: 'Vegyes napló', leiras: 'Vegyes bizonylatok, utólagos összepontozás', ikon: 'shuffle-outline',
        ut: '/naplo/naplok', query: { ful: 'vegyes' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'nyito', cim: 'Nyitó napló', leiras: 'Az évnyitás tételei', ikon: 'log-in-outline',
        ut: '/naplo/naplok', query: { ful: 'nyito' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'zaro', cim: 'Záró napló', leiras: 'Az évzárás tételei', ikon: 'log-out-outline',
        ut: '/naplo/naplok', query: { ful: 'zaro' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'osszes', cim: 'Összes napló', leiras: 'Minden bizonylat egy listában', ikon: 'albums-outline',
        ut: '/naplo/naplok', query: { ful: 'osszes' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'naplofokonyv', cim: 'Naplófőkönyv', leiras: 'Rovatos napló, oszloponkénti összesítéssel', ikon: 'book-outline',
        ut: '/naplo/naplok', query: { ful: 'naplofokonyv' }, mod: 'egyszeres', jog: '/naplo/naplok' },
      { kulcs: 'kivonat', cim: 'Főkönyvi kivonat', leiras: 'Számlánkénti nyitó, forgalom, egyenleg', ikon: 'book-outline',
        ut: '/naplo/naplok', query: { ful: 'kivonat' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'karton', cim: 'Főkönyvi kartonok', leiras: 'Egy számla tételei göngyölt egyenleggel', ikon: 'reader-outline',
        ut: '/naplo/naplok', query: { ful: 'karton' }, mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'gyorsmerleg', cim: 'Gyorsmérleg', leiras: 'Eszközök és források számlánként', ikon: 'scale-outline',
        ut: '/naplo/naplok', query: { ful: 'gyorsmerleg' }, mod: 'kettos', jog: '/naplo/naplok' },
      // Csak egyszeres könyvelésnél: kettősben a pénztár mozgásai a Pénztár/Bank naplóban
      // (forrás: Pénztár), a pénztár-számla kartonjából.
      { kulcs: 'hazipenztar', cim: 'Házipénztár', leiras: 'Pénztári mozgások egyenleggel', ikon: 'wallet-outline',
        ut: '/naplo/naplok', query: { ful: 'hazipenztar' }, mod: 'egyszeres', jog: '/naplo/naplok' },
      //  Mindkét módban: a Tárgyi eszköz modul adatából (írni ott lehet, itt a napló és a mozgástábla).
      { kulcs: 'befektetett', cim: 'Befektetett eszközök', leiras: 'Eszközmozgások, écs és értékvesztés; mozgástábla', ikon: 'construct-outline',
        ut: '/naplo/naplok', query: { ful: 'eszkoz' }, jog: '/naplo/naplok' },
      // Az eredmény és az ÁFA-analitika MINDKÉT módban ugyanitt, ugyanazzal a névvel – a mód csak
      // azt dönti el, melyik kimutatás nyílik (egyszeres: naplófőkönyvből; kettős: főkönyvből).
      { kulcs: 'eredmeny', cim: 'Eredménykimutatás', leiras: 'Bevételek, költségek, eredmény', ikon: 'trending-up-outline',
        ut: '/naplo/listak', query: { nezet: 'eredmeny' }, utKettos: '/naplo/kettos', queryKettos: { nezet: 'eredmeny' }, jog: '/naplo/listak' },
      { kulcs: 'afa', cim: 'ÁFA-analitika', leiras: 'Levonható és fizetendő ÁFA', ikon: 'pie-chart-outline',
        ut: '/naplo/listak', query: { nezet: 'afa' }, utKettos: '/naplo/kettos', queryKettos: { nezet: 'afa' }, jog: '/naplo/listak' },
      { kulcs: 'merleg', cim: 'Mérleg', leiras: 'Eszközök és források', ikon: 'scale-outline',
        ut: '/naplo/kettos', query: { nezet: 'merleg' }, mod: 'kettos', jog: '/naplo/listak' },
      { kulcs: 'penzforgalom', cim: 'Pénzforgalom', leiras: 'Havi bevétel és kiadás', ikon: 'swap-vertical-outline',
        ut: '/naplo/listak', query: { nezet: 'penzforgalom' }, mod: 'egyszeres', jog: '/naplo/listak' },
      { kulcs: 'rovatanalitika', cim: 'Rovatanalitika', leiras: 'Rovatonkénti összesítés', ikon: 'list-outline',
        ut: '/naplo/listak', query: { nezet: 'rovat' }, mod: 'egyszeres', jog: '/naplo/listak' },
      { kulcs: 'partnerosszesito', cim: 'Partnerösszesítő', leiras: 'Forgalom partnerenként', ikon: 'people-circle-outline',
        ut: '/naplo/listak', query: { nezet: 'partner' }, mod: 'egyszeres', jog: '/naplo/listak' },
    ],
  },
  {
    kulcs: 'bank', cim: 'Bank', ikon: 'card-outline',
    pontok: [
      //  A bank az ügyfélnél kapcsolható (Ügyfelek → Bank használata): akinek nincs bankja,
      //  annak ez a csoport sem jelenik meg.
      { kulcs: 'bankszamlak', cim: 'Bankszámlák', leiras: 'Összekötés, szinkron, meghatalmazás', ikon: 'card-outline', ut: '/naplo/bank', bankFuggo: true, jog: '/naplo/bank' },
      { kulcs: 'kontir2', cim: 'Kontírozás', leiras: 'Javaslat, párosítás, könyvelés, RLB-feladás', ikon: 'git-compare-outline', ut: '/naplo/kontir', bankFuggo: true, jog: '/naplo/kontir' },
      { kulcs: 'banknaplo', cim: 'Bank napló', leiras: 'A banki tételek naplója', ikon: 'cash-outline',
        ut: '/naplo/naplok', query: { ful: 'bank' }, bankFuggo: true, jog: '/naplo/naplok' },
    ],
  },
  {
    kulcs: 'hatosag', cim: 'Hatóság', ikon: 'shield-checkmark-outline',
    pontok: [
      { kulcs: 'tarhely', cim: 'Tárhely', leiras: 'Hivatalos üzenetek (KAÜ)', ikon: 'mail-unread-outline', ut: '/naplo/tarhely', jog: '/naplo/tarhely' },
      { kulcs: 'adoszamla', cim: 'Adófolyószámla', leiras: 'NAV egyenleg, hiányzó bevallások', ikon: 'server-outline', ut: '/naplo/adoszamla', jog: '/naplo/adoszamla' },
      { kulcs: 'eafa', cim: 'eÁFA', leiras: 'ÁFA-analitika és gépi bevallás', ikon: 'document-text-outline', ut: '/naplo/eafa', jog: '/naplo/eafa' },
      { kulcs: 'eonk', cim: 'e-Önkormányzat', leiras: 'Helyi adók egyenlege', ikon: 'business-outline', ut: '/naplo/eonkormanyzat', jog: '/naplo/eonkormanyzat' },
      { kulcs: 'munkaterv', cim: 'Munkaterv', leiras: 'Beadandó bevallások ügyfelenként', ikon: 'calendar-number-outline', ut: '/naplo/munkaterv', jog: '/naplo/munkaterv' },
      { kulcs: 'bevallasok', cim: 'Bevallások (M2M)', leiras: 'NAV-nyomtatványok kitöltése, ellenőrzése, beküldése M2M-en', ikon: 'document-attach-outline', ut: '/naplo/bevallasok', jog: '/naplo/bevallasok' },
      //  A beadott bevallások és a tárhelyre érkezett NAV-igazolásuk EGY helyen: ebből látszik,
      //  hogy a beküldés tényleg megérkezett (feladási + letöltési igazolás).
      { kulcs: 'bekuldott', cim: 'Beküldött bevallások', leiras: 'Beadott bevallások a tárhelyről érkezett feladási és letöltési igazolással', ikon: 'checkmark-done-outline', ut: '/naplo/bevallasok', query: { nezet: 'bekuldott' }, jog: '/naplo/bevallasok' },
      //  Bármilyen úton (ÁNYK, ONYA, gépi) beküldött bevallás visszakérése a NAV Ügyfélportáljáról, PDF-fel.
      { kulcs: 'upobevallas', cim: 'NAV-ban beküldött bevallások', leiras: 'Az Ügyfélportálról visszakérve egy időszakra, PDF-fel', ikon: 'cloud-download-outline', ut: '/naplo/upo-bevallasok', jog: '/naplo/adoszamla' },
    ],
  },
  {
    kulcs: 'szerviz', cim: 'Szervíz', ikon: 'construct-outline',
    pontok: [
      //  A NAV technikai felhasználó (Online Számla / pénztárgép / eÁFA) beállítása – teszt és éles
      //  hitelesítővel. Korábban a Törzsadatok között volt, de ez beállítás, nem törzsadat.
      { kulcs: 'osa', cim: 'NAV technikai felhasználó', leiras: 'Online Számla, pénztárgép és eÁFA hitelesítője – teszt és éles', ikon: 'key-outline',
        ut: '/naplo/torzsek', query: { nezet: 'osa' }, jog: '/naplo/torzsek' },
      //  A NAV Számlázó képernyője KIKERÜLT a menüből: a számlaképek letöltése az Online Számla
      //  kimenő szinkronjával automatikus (Szervíz → NAV technikai felhasználó → „Számlaképek
      //  automatikus letöltése"), egyenként pedig a számlaválasztóból kérhető. Az útvonal
      //  (/naplo/nav) megmaradt, hogy a meglévő hivatkozások ne törjenek el.
      { kulcs: 'szinkron', cim: 'Szinkronizálás', leiras: 'Automatikus lekérések időzítése', ikon: 'timer-outline', ut: '/naplo/szinkron', jog: '/naplo/szinkron' },
      //  ÜZEMSZÜNET-FIGYELŐ: a külső rendszerek (NAV, Ügyfélkapu, bank, e-Önkormányzat) elérhetősége,
      //  tervezett karbantartás és kiesés jelzése. A funkciót nem tiltja, csak figyelmeztet.
      //  A jog a Szinkronizálásé: ugyanaz a szerviz-kör látja (új backend-jogot nem vezetünk be).
      { kulcs: 'uzemszunet', cim: 'Üzemszünet-figyelő', leiras: 'Külső rendszerek elérhetősége, tervezett karbantartás', ikon: 'pulse-outline', ut: '/naplo/uzemszunet', jog: '/naplo/szinkron' },
      //  A főkönyv és az analitika egyeztetése: ahol a kettő nem egyezik, ott valamit csak az
      //  egyik oldalon rögzítettünk. A javítható eseteket egy gombbal helyre is teszi.
      //  (A Listák közül került ide: ez nem kimutatás, hanem karbantartó eszköz.)
      { kulcs: 'egyeztetes', cim: 'Hibakeresés', leiras: 'Vevő/szállító főkönyv–analitika egyeztetés', ikon: 'search-outline',
        ut: '/naplo/egyeztetes', mod: 'kettos', jog: '/naplo/naplok' },
      { kulcs: 'fiokok', cim: 'Ügyfélkapu-fiókok', leiras: 'KAÜ-belépések és TOTP-kódok', ikon: 'finger-print-outline', ut: '/naplo/fiokok', jog: '/naplo/fiokok' },
      { kulcs: 'nav-kapcsolat', cim: 'NAV-kapcsolat (M2M)', leiras: 'A gépi beküldés hitelesítője – teszt és éles környezet külön', ikon: 'link-outline', ut: '/naplo/nav-kapcsolat', jog: '/naplo/nav-kapcsolat' },
      { kulcs: 'tanusitvany', cim: 'E-mail aláírás', leiras: 'Kimenő levelek aláíró tanúsítványa', ikon: 'ribbon-outline', ut: '/naplo/tanusitvany', jog: '/naplo/tanusitvany' },
      { kulcs: 'emailelkapo', cim: 'E-mail elkapó', leiras: 'Számlák e-mailben, beérkezett levelek', ikon: 'mail-unread-outline', ut: '/naplo/email-elkapo', jog: '/naplo/email-elkapo' },
      // A globális admin (admin-katalogus.ts) a Könyvelés saját beállításaival és az általános résszel.
      { kulcs: 'admin', cim: 'Adminisztráció', leiras: 'Jogosultságok, modul témája, rendszer', ikon: 'settings-outline', ut: '/admin', jog: '/admin' },
    ],
  },
];

/** Egy menüpont útvonala és paraméterei az adott könyvelési módban. */
export function menupontCel(p: NaploMenupont, mod: string): { ut: string; query?: Record<string, string> } {
  if (mod === 'kettos' && p.utKettos) return { ut: p.utKettos, query: p.queryKettos };
  return { ut: p.ut, query: p.query };
}

/** Látszik-e a menüpont: jog, könyvelési mód, ügyféltípus és a bank-kapcsoló szerint. */
export function menupontLathato(p: NaploMenupont, mod: string, ugyfelTipus: string | undefined, szabad: (url: string) => boolean, bank = true): boolean {
  if (p.mod && p.mod !== mod) return false;
  if (p.ugyfelTipus && p.ugyfelTipus !== ugyfelTipus) return false;
  if (p.bankFuggo && !bankVan(mod, bank)) return false;
  return szabad(p.jog);
}

/*  KETTŐS KÖNYVVITELBEN MINDIG VAN BANK – ott a pénzintézeti számla a főkönyv része, nem
 *  választás kérdése. A „Bank használata" kapcsoló csak az EGYSZERES könyvvitelnél érdekes,
 *  ahol sok ügyfélnél tényleg nincs mit bankolni. */
export const bankVan = (mod: string, bankKapcsolo: boolean | undefined): boolean =>
  mod === 'kettos' || !!bankKapcsolo;

/**
 *  Melyik képernyő-jog kell egy NAPLO-útvonalhoz (az oldalőr ebből dönt). Az útvonal és a
 *  `nezet`/`ful` paraméter együtt azonosít: a kettős nézet törzsadat-részéhez a Törzsadatok,
 *  a kimutatásaihoz a Listák joga kell.
 */
export function utvonalJoga(szakasz: string, query: Record<string, any>): string[] {
  const ut = '/naplo/' + szakasz;
  const talalat: string[] = [];
  for (const f of NAPLO_FOMENUK) {
    for (const p of f.pontok) {
      for (const [u, qq] of [[p.ut, p.query], [p.utKettos, p.queryKettos]] as [string | undefined, Record<string, string> | undefined][]) {
        if (u !== ut) continue;
        const illik = !qq || Object.entries(qq).every(([k, v]) => String(query?.[k] ?? '') === v);
        if (illik) talalat.push(p.jog);
      }
    }
  }
  if (talalat.length) return [...new Set(talalat)];
  // A kettős nézet paraméter nélkül a könyvelést nyitja.
  if (szakasz === 'kettos') return ['/naplo/konyveles'];
  //  A menüből kivezetett NAV Számlázó képernyő továbbra is elérhető közvetlen hivatkozással,
  //  a saját jogával.
  if (szakasz === 'nav') return ['/naplo/nav'];
  // Egy bevallás kitöltője a Bevallások (NAV-nyomtatványok) képernyő joga.
  if (szakasz.startsWith('bevallas')) return ['/naplo/bevallasok'];
  return [ut];
}
